import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import PickCard from "@/components/PickCard";
import ParlayOfDayCard from "@/components/ParlayOfDayCard";
import Disclaimer from "@/components/Disclaimer";
import PicksTabs from "@/components/PicksTabs";
import PerformancePanel, { type ParlayStats } from "@/components/PerformancePanel";
import { Button } from "@/components/ui/button";
import { bettingDayBounds, SPORT_META, BET_LABEL, todayBettingDatePT, nextBettingDatePT } from "@/lib/picks";
import { fetchPickAccessMap } from "@/lib/access";
import { ROLLOVER_HINT } from "@/lib/slate";
import SegmentedToggle from "@/components/SegmentedToggle";
import { Sparkles, Trophy, Activity, Clock, TrendingUp, Sunrise } from "lucide-react";
import { summarizeLegs } from "@/components/LegStatusBadge";
import MobileStatCarousel, { type CarouselStat } from "@/components/dashboard/MobileStatCarousel";
import TodaySnapshot from "@/components/dashboard/TodaySnapshot";
import HotPickHero from "@/components/dashboard/HotPickHero";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";

type Stats = { wins: number; losses: number; pushes: number; units: number; roi: number; total_picks: number };

export default function Dashboard() {
  const { isPro, isAdmin, user } = useAuth();
  const [top5, setTop5] = useState<any[]>([]);
  const [todayCount, setTodayCount] = useState(0);
  const [tomorrowTop5, setTomorrowTop5] = useState<any[]>([]);
  const [tomorrowCount, setTomorrowCount] = useState(0);
  const [activeSlate, setActiveSlate] = useState<"today" | "tomorrow">("today");
  const [platformStats, setPlatformStats] = useState<Stats>({ wins: 0, losses: 0, pushes: 0, units: 0, roi: 0, total_picks: 0 });
  const [userStats, setUserStats] = useState<Stats>({ wins: 0, losses: 0, pushes: 0, units: 0, roi: 0, total_picks: 0 });
  const [recent32, setRecent32] = useState<{ wins: number; losses: number; pushes: number; units: number; total: number } | null>(null);
  const [parlay, setParlay] = useState<any | null>(null);
  const [parlayLegs, setParlayLegs] = useState<any[] | null>(null);
  const [parlayLegTimes, setParlayLegTimes] = useState<Date[]>([]);
  const [hasParlayEntitlement, setHasParlayEntitlement] = useState(false);
  const [yesterday, setYesterday] = useState<{ picks: any[]; wins: number; losses: number; pushes: number; units: number; date: string } | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const EMPTY_PARLAY: ParlayStats = { wins: 0, losses: 0, pushes: 0, units: 0, settled: 0, avgOddsAmerican: null, biggestWinUnits: 0 };
  const [platformParlays, setPlatformParlays] = useState<ParlayStats>(EMPTY_PARLAY);
  const [userParlays, setUserParlays] = useState<ParlayStats>(EMPTY_PARLAY);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const bounds = bettingDayBounds();
      const bettingDate = todayBettingDatePT();
      const { data: top5Data } = await supabase.from("picks").select("*, sports(code)").eq("status", "published").eq("is_top5", true).filter("betting_date", "eq", bettingDate).is("deleted_at", null).order("confidence_score", { ascending: false }).limit(5);
      const top5Ids = (top5Data ?? []).map((p) => p.id);
      const accessMap = await fetchPickAccessMap(user.id, bounds.start, bounds.end, false, true, top5Ids);
      setTop5((top5Data ?? []).map((p) => ({ ...p, access: accessMap.get(p.id) })));

      // Most recent published timestamp (for "Updated" trust signal)
      const latestTs = (top5Data ?? []).reduce<string | null>((acc, p) => {
        const t = p.published_at as string | null;
        if (!t) return acc;
        if (!acc || new Date(t) > new Date(acc)) return t;
        return acc;
      }, null);
      if (latestTs) setUpdatedAt(new Date(latestTs));

      const { count } = await supabase.from("picks").select("id", { count: "exact", head: true }).eq("status", "published").filter("betting_date", "eq", bettingDate).is("deleted_at", null);
      setTodayCount(count ?? 0);

      // Tomorrow slate (so we can show "Tomorrow's Top 5" before rollover when admins pre-publish)
      const tomorrowDate = nextBettingDatePT();
      const { data: tmwTop5 } = await supabase.from("picks").select("*, sports(code)").eq("status", "published").eq("is_top5", true).filter("betting_date", "eq", tomorrowDate).is("deleted_at", null).order("confidence_score", { ascending: false }).limit(5);
      const tmwIds = (tmwTop5 ?? []).map((p) => p.id);
      if (tmwIds.length > 0) {
        // Use a wide bounds window so access checks include tomorrow's published_at instants
        const tmwAccess = await fetchPickAccessMap(user.id, bounds.start, new Date(Date.now() + 48 * 3600 * 1000).toISOString(), false, true, tmwIds);
        setTomorrowTop5((tmwTop5 ?? []).map((p) => ({ ...p, access: tmwAccess.get(p.id) })));
      } else {
        setTomorrowTop5([]);
      }
      const { count: tmwCount } = await supabase.from("picks").select("id", { count: "exact", head: true }).eq("status", "published").filter("betting_date", "eq", tomorrowDate).is("deleted_at", null);
      setTomorrowCount(tmwCount ?? 0);
      // If today has nothing but tomorrow does, surface tomorrow by default
      if ((count ?? 0) === 0 && (tmwCount ?? 0) > 0) setActiveSlate("tomorrow");

      const { data: stats } = await supabase.rpc("record_stats", { _uid: null, _sport_code: null, _bet_type: null, _start_ts: null, _end_ts: null });
      const row = stats?.[0];
      setPlatformStats({ wins: Number(row?.wins ?? 0), losses: Number(row?.losses ?? 0), pushes: Number(row?.pushes ?? 0), units: Number(row?.units ?? 0), roi: Number(row?.roi ?? 0), total_picks: Number(row?.total_picks ?? 0) });

      const { data: userRecord } = await supabase.rpc("user_record_stats", { _uid: user.id });
      const userRow = userRecord?.[0];
      setUserStats({
        wins: Number(userRow?.wins ?? 0),
        losses: Number(userRow?.losses ?? 0),
        pushes: Number(userRow?.pushes ?? 0),
        units: Number(userRow?.units ?? 0),
        roi: Number(userRow?.roi ?? 0),
        total_picks: Number(userRow?.total_picks ?? 0),
      });

      // Last 32 settled picks for credibility strip
      const { data: recent } = await supabase
        .from("picks")
        .select("result, units_delta")
        .eq("status", "published")
        .is("deleted_at", null)
        .in("result", ["won", "lost", "pushed"])
        .order("published_at", { ascending: false })
        .limit(32);
      if (recent && recent.length > 0) {
        const w = recent.filter((p) => p.result === "won").length;
        const l = recent.filter((p) => p.result === "lost").length;
        const ps = recent.filter((p) => p.result === "pushed").length;
        const u = recent.reduce((s, p) => s + Number(p.units_delta ?? 0), 0);
        setRecent32({ wins: w, losses: l, pushes: ps, units: u, total: recent.length });
      }

      const today = bettingDate;
      const { data: pod } = await supabase
        .from("parlay_of_day")
        .select("*")
        .eq("status", "published")
        .filter("betting_date", "eq", today)
        .order("published_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      setParlay(pod ?? null);
      if (pod) {
        const { data: canAccess } = await supabase.rpc("user_can_access_parlay", { _uid: user.id, _parlay_id: pod.id });
        setHasParlayEntitlement(Boolean(canAccess));
        const { data: legs } = await supabase
          .from("parlay_of_day_legs")
          .select("*, sports(code)")
          .eq("parlay_id", pod.id)
          .order("leg_order");
        setParlayLegs(legs ?? []);
        const hasTimes = (legs ?? []).some((l: any) => !!l.commence_time);
        if (!hasTimes) {
          const { data: times } = await supabase.rpc("parlay_leg_start_times", { _parlay_id: pod.id });
          const ts = ((times ?? []) as Array<{ commence_time: string | null }>)
            .map((r) => (r.commence_time ? new Date(r.commence_time) : null))
            .filter((d): d is Date => !!d && !isNaN(d.getTime()));
          setParlayLegTimes(ts);
        } else {
          setParlayLegTimes(
            (legs ?? [])
              .map((l: any) => (l.commence_time ? new Date(l.commence_time) : null))
              .filter((d: Date | null): d is Date => !!d && !isNaN(d.getTime())),
          );
        }
      }

      // Yesterday's results
      const y = new Date();
      y.setDate(y.getDate() - 1);
      const yBounds = bettingDayBounds(y);
      const { data: yPicks } = await supabase
        .from("picks")
        .select("*, sports(code)")
        .eq("status", "published")
        .is("deleted_at", null)
        .filter("betting_date", "eq", new Date(yBounds.start).toISOString().slice(0, 10))
        .in("result", ["won", "lost", "pushed"])
        .order("confidence_score", { ascending: false });
      const yList = yPicks ?? [];
      const wins = yList.filter((p) => p.result === "won").length;
      const losses = yList.filter((p) => p.result === "lost").length;
      const pushes = yList.filter((p) => p.result === "pushed").length;
      const units = yList.reduce((s, p) => s + Number(p.units_delta ?? 0), 0);
      setYesterday({
        picks: yList,
        wins,
        losses,
        pushes,
        units,
        date: new Date(yBounds.start).toLocaleDateString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric" }),
      });

      // Parlay performance (tracked separately from straight picks)
      const amToDec = (a: number) => (a > 0 ? 1 + a / 100 : 1 + 100 / Math.abs(a));
      const decToAm = (d: number) => (d >= 2 ? Math.round((d - 1) * 100) : -Math.round(100 / (d - 1)));
      const aggregateParlays = (rows: { result: string | null; units_delta: number | null; total_odds: number | null }[]): ParlayStats => {
        const settled = rows.filter((r) => r.result === "won" || r.result === "lost" || r.result === "pushed");
        const wins = settled.filter((r) => r.result === "won").length;
        const losses = settled.filter((r) => r.result === "lost").length;
        const pushes = settled.filter((r) => r.result === "pushed").length;
        const units = settled.reduce((s, r) => s + Number(r.units_delta ?? 0), 0);
        const oddsRows = settled.filter((r) => r.total_odds != null);
        const avgDec = oddsRows.length > 0 ? oddsRows.reduce((s, r) => s + amToDec(Number(r.total_odds)), 0) / oddsRows.length : null;
        const avgOddsAmerican = avgDec !== null ? decToAm(avgDec) : null;
        const biggestWinUnits = settled.reduce((m, r) => Math.max(m, Number(r.units_delta ?? 0)), 0);
        return { wins, losses, pushes, units, settled: settled.length, avgOddsAmerican, biggestWinUnits };
      };

      const { data: allParlays } = await supabase
        .from("parlay_of_day")
        .select("result, units_delta, total_odds")
        .eq("status", "published")
        .in("result", ["won", "lost", "pushed"]);
      setPlatformParlays(aggregateParlays(allParlays ?? []));

      const { data: ups } = await supabase.rpc("user_parlay_stats", { _uid: user.id });
      const upRow: any = ups?.[0];
      setUserParlays({
        wins: Number(upRow?.wins ?? 0),
        losses: Number(upRow?.losses ?? 0),
        pushes: Number(upRow?.pushes ?? 0),
        units: Number(upRow?.units ?? 0),
        settled: Number(upRow?.settled ?? 0),
        avgOddsAmerican: upRow?.avg_odds_american != null ? Number(upRow.avg_odds_american) : null,
        biggestWinUnits: Number(upRow?.biggest_win_units ?? 0),
      });
    })();
  }, [user?.id, isAdmin, isPro]);

  const showUpgradeCta = !isPro && !isAdmin;

  // Lightweight editorial insight derived from today's Top 5 (no backend changes)
  const editorialInsight = useMemo(() => {
    if (!top5 || top5.length === 0) return null;
    const counts: Record<string, number> = {};
    for (const p of top5) {
      const code = p.sports?.code ?? "—";
      counts[code] = (counts[code] ?? 0) + 1;
    }
    const [topSport] = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const totalsCount = top5.filter((p) => String(p.bet_type ?? "").toLowerCase().includes("total")).length;
    if (totalsCount >= 3) return `Model leaning totals-heavy across today's board.`;
    if (topSport && topSport[1] >= 3) return `Strongest edge currently in ${topSport[0]}.`;
    const avgConf = Math.round(top5.reduce((s, p) => s + Number(p.confidence_score ?? 0), 0) / top5.length);
    return `Average model confidence on today's Top 5: ${avgConf}/100.`;
  }, [top5]);

  const updatedLabel = useMemo(() => {
    if (!updatedAt) return null;
    return updatedAt.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: "America/Los_Angeles",
      timeZoneName: "short",
    });
  }, [updatedAt]);

  const parlayStatusLabel = useMemo(() => {
    if (!parlay) return "Pending";
    const r = parlay.result;
    if (r === "won") return "Won";
    if (r === "lost") return "Lost";
    if (r === "pushed") return "Push";
    if (r === "void") return "Void";
    const legs = parlayLegs ?? [];
    const summary = summarizeLegs(legs);
    if (summary.settled > 0) return `${summary.won}/${summary.total} Won`;
    const fromLegs = legs
      .map((l: any) => (l.commence_time ? new Date(l.commence_time) : null))
      .filter((d): d is Date => !!d && !isNaN(d.getTime()));
    const times = fromLegs.length > 0 ? fromLegs : parlayLegTimes;
    if (times.length === 0) return "Live";
    const now = Date.now();
    const started = times.filter((d) => d.getTime() <= now).length;
    if (started === 0) return "Not Started";
    if (started === times.length) return "In Play";
    return `${started}/${times.length} Started`;
  }, [parlay, parlayLegs, parlayLegTimes]);

  return (
    <div className="container py-6 sm:py-8 lg:py-10">
      <VerifyEmailBanner />
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6 sm:mb-7">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Welcome back</h1>
          <p className="text-sm text-muted-foreground mt-1">{user?.email}</p>
        </div>
        {showUpgradeCta && (
          <Link to="/pricing" className="block">
            <Button className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-95">
              <Sparkles className="w-4 h-4 mr-2" /> Go Pro — $19.99/mo
            </Button>
            <p className="text-[11px] text-muted-foreground mt-1.5 text-right">Includes all picks + Parlay of the Day</p>
          </Link>
        )}
      </div>

      {/* Mobile-only Today Snapshot strip */}
      {(() => {
        const settledToday = top5.filter((p) => p.result === "won" || p.result === "lost" || p.result === "pushed");
        const wins = settledToday.filter((p) => p.result === "won").length;
        const losses = settledToday.filter((p) => p.result === "lost").length;
        const pushes = settledToday.filter((p) => p.result === "pushed").length;
        const unitsToday = settledToday.reduce((s, p) => s + Number(p.units_delta ?? 0), 0);
        const top = [...top5].sort((a, b) => Number(b.confidence_score ?? 0) - Number(a.confidence_score ?? 0))[0];
        const bestEdge = top
          ? `${top.title || top.teams || (BET_LABEL[top.bet_type] ?? "Top pick")} · ${top.confidence_score ?? "—"} conf`
          : null;
        const sports = Array.from(new Set(top5.map((p) => p.sports?.code).filter(Boolean))) as string[];
        return (
          <div className="md:hidden">
            <TodaySnapshot
              record={settledToday.length > 0 ? { wins, losses, pushes } : null}
              units={settledToday.length > 0 ? unitsToday : null}
              bestEdgeLabel={bestEdge}
              activeSports={sports}
            />
            {top && (
              <HotPickHero
                pick={top}
                sportCode={top.sports?.code ?? "NBA"}
                locked={top.access?.is_locked ?? true}
              />
            )}
          </div>
        );
      })()}

      {/* Metric cards — desktop grid */}
      <div className="hidden md:grid sm:grid-cols-3 gap-3 sm:gap-4 mb-4">
        <StatCard label="Picks Published Today" value={`${todayCount}`} />
        <StatCard label="Top 5 Published" value={`${top5.length}`} />
        <StatCard label="Parlay of the Day" value={parlayStatusLabel} />
      </div>

      {/* Metric cards — mobile carousel */}
      <div className="md:hidden mb-4">
        {(() => {
          const items: CarouselStat[] = [
            { key: "picks", label: "Picks Published Today", value: `${todayCount}` },
            { key: "top5", label: "Top 5 Published", value: `${top5.length}` },
            { key: "parlay", label: "Parlay of the Day", value: parlayStatusLabel },
          ];
          return <MobileStatCarousel items={items} />;
        })()}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 sm:gap-5 mb-7 sm:mb-8">
        <PerformancePanel
          title="Platform Performance"
          subtitle="Overall results across all published settled picks and parlays."
          scope="platform"
          picks={platformStats}
          parlays={platformParlays}
        />
        <PerformancePanel
          title={isAdmin ? "Admin Access Performance" : "Your Performance"}
          subtitle={
            isAdmin
              ? "As an admin you have full access, so this mirrors platform-wide results."
              : "Your stats track picks and parlays you unlocked or accessed."
          }
          scope="user"
          picks={userStats}
          parlays={userParlays}
          emptyParlaysHint={
            isAdmin || isPro
              ? "No parlays settled yet"
              : "You haven't unlocked any settled parlays yet."
          }
        />
      </div>

      {/* Trust / credibility strip */}
      {recent32 && (
        <div className="te-card px-4 py-3 mb-7 sm:mb-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs sm:text-sm">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Activity className="w-3.5 h-3.5 text-primary" />
            <span className="font-medium text-foreground tabular-nums">{recent32.wins}-{recent32.losses}{recent32.pushes ? `-${recent32.pushes}` : ""}</span>
            <span>last {recent32.total} settled</span>
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <TrendingUp className={`w-3.5 h-3.5 ${recent32.units >= 0 ? "text-success" : "text-danger"}`} />
            <span className={`font-medium tabular-nums ${recent32.units >= 0 ? "text-success" : "text-danger"}`}>
              {recent32.units >= 0 ? "+" : ""}{recent32.units.toFixed(1)}u
            </span>
            <span>tracked</span>
          </div>
          {(() => {
            const decided = recent32.wins + recent32.losses;
            if (decided === 0) return null;
            const winRate = Math.round((recent32.wins / decided) * 100);
            return (
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <span className="font-medium text-foreground tabular-nums">{winRate}%</span>
                <span>win rate</span>
              </div>
            );
          })()}
          {updatedLabel && (
            <div className="ml-auto flex items-center gap-1.5 text-muted-foreground">
              <Clock className="w-3.5 h-3.5" />
              <span>Updated {updatedLabel}</span>
            </div>
          )}
        </div>
      )}

      {parlay && (
        <div className="mb-8">
          <ParlayOfDayCard
            parlay={parlay}
            legs={parlayLegs}
            locked={!isAdmin && !isPro && !hasParlayEntitlement}
            canBuyParlay={!isAdmin && !isPro}
            parlayPurchased={!isAdmin && !isPro && hasParlayEntitlement}
            isPremium={isAdmin || isPro}
          />
        </div>
      )}

      {/* Top 5 — slate-aware (today / tomorrow) */}
      {(() => {
        const showToggle = todayCount > 0 || tomorrowCount > 0;
        const slate = activeSlate;
        const list = slate === "tomorrow" ? tomorrowTop5 : top5;
        const heading = slate === "tomorrow" ? "Tomorrow's Top 5" : "Today's Top 5";
        return (
          <>
            <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
              <div>
                <h2 className="font-display text-2xl font-bold flex items-center gap-2">
                  <Trophy className="text-accent w-6 h-6" /> {heading}
                </h2>
                {editorialInsight && (
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 italic">{editorialInsight}</p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {showToggle && (
                  <SegmentedToggle
                    ariaLabel="Slate"
                    options={[
                      { value: "today", label: "Today" },
                      { value: "tomorrow", label: "Tomorrow", icon: Sunrise },
                    ]}
                    value={slate}
                    onChange={(v) => setActiveSlate(v as "today" | "tomorrow")}
                  />
                )}
                <PicksTabs />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground/80 mb-4">{ROLLOVER_HINT}</p>

            {list.length === 0 ? (
              <div className="text-center text-muted-foreground py-10 te-card">
                {slate === "tomorrow"
                  ? "No Top 5 has been scheduled for tomorrow yet."
                  : <>No Top 5 picks have been flagged for today yet. <Link to="/picks" className="underline">Browse all picks →</Link></>}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {(() => {
                  const freeIdx = list.findIndex((p) => p.access?.is_free_pick);
                  const ordered = freeIdx > 0 ? [list[freeIdx], ...list.filter((_, i) => i !== freeIdx)] : list;
                  return ordered.map((p, i) => (
                    <PickCard key={p.id} pick={p} sportCode={p.sports?.code ?? "NBA"} rank={i + 1} locked={p.access?.is_locked ?? true} />
                  ));
                })()}
              </div>
            )}
          </>
        );
      })()}

      {yesterday && yesterday.picks.length > 0 && (
        <div className="mt-10 sm:mt-12">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="font-display text-2xl font-bold">Yesterday's Results</h2>
              <p className="text-[11px] uppercase text-muted-foreground tracking-wider mt-1">{yesterday.date} PT · {yesterday.picks.length} settled</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="te-chip border border-success/40 bg-success/15 text-success font-bold tabular-nums">{yesterday.wins}W</span>
              <span className="te-chip border border-danger/40 bg-danger/15 text-danger font-bold tabular-nums">{yesterday.losses}L</span>
              {yesterday.pushes > 0 && <span className="te-chip border border-muted-foreground/30 bg-muted/15 text-muted-foreground font-bold tabular-nums">{yesterday.pushes}P</span>}
              <span className={`te-chip border font-bold tabular-nums ${yesterday.units >= 0 ? "border-success/40 bg-success/15 text-success" : "border-danger/40 bg-danger/15 text-danger"}`}>
                {yesterday.units >= 0 ? "+" : ""}{yesterday.units.toFixed(2)}u
              </span>
            </div>
          </div>
          <div className="te-card divide-y divide-border/60 overflow-hidden">
            {yesterday.picks.slice(0, 8).map((p) => {
              const meta = SPORT_META[p.sports?.code ?? "NBA"] ?? SPORT_META.NBA;
              const won = p.result === "won";
              const lost = p.result === "lost";
              const resColor = won ? "text-success" : lost ? "text-danger" : "text-muted-foreground";
              const resBadge = won
                ? "border-success/40 bg-success/15 text-success"
                : lost
                ? "border-danger/40 bg-danger/15 text-danger"
                : "border-muted-foreground/30 bg-muted/15 text-muted-foreground";
              const resLabel = won ? "Won" : lost ? "Lost" : "Push";
              const u = Number(p.units_delta ?? 0);
              return (
                <div key={p.id} className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 hover:bg-secondary/30 transition">
                  <span className={`te-chip border ${meta.color} shrink-0 hidden sm:inline-flex`}>{meta.icon} {meta.label}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate text-sm sm:text-base">{p.title}</div>
                    {p.teams && <div className="text-xs text-muted-foreground truncate">{p.teams}</div>}
                  </div>
                  <span className={`te-chip border text-[11px] font-bold tracking-wider shrink-0 ${resBadge}`}>{resLabel.toUpperCase()}</span>
                  <div className={`font-display font-extrabold text-sm sm:text-base tabular-nums w-16 text-right shrink-0 ${resColor}`}>
                    {u >= 0 ? "+" : ""}{u.toFixed(2)}u
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-10 sm:mt-12">
        <Disclaimer compact />
      </div>
    </div>
  );
}

function StatCard({ label, value, accent, compact }: { label: string; value: string; accent?: boolean; compact?: boolean }) {
  return (
    <div className={compact ? "rounded-xl border border-border/70 bg-secondary/20 p-3" : "te-card p-4 sm:p-5"}>
      <div className="text-[10px] sm:text-xs uppercase text-muted-foreground tracking-wider">{label}</div>
      <div className={`font-display ${compact ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl"} font-bold mt-1 tabular-nums ${accent ? "te-grad-text" : ""}`}>{value}</div>
    </div>
  );
}
